/**
 * buildNextSessionLine — pure decision logic behind the public detail page's
 * "Next session: Tonight · 6:00 PM – 7:00 PM · 12 spots left" line. Assembly
 * only: the caller (a client component) supplies the program payload pieces,
 * the availability response (or null while pending / after a failed or 403
 * fetch, which degrades to a time-only line), and gym-local "now" parts. No
 * fetching, no clock, no timezone reads — unit-testable without rendering the
 * 1,700-line page.
 */

import {
  nextOpenGymSession,
  formatSessionDayLabel,
  type GymNowParts,
  type OpenGymSessionSlot,
} from './nextOpenGymSession';
import { dropInSpotsForDate, type DropInAvailabilityRule } from './dropInSpotsForDate';

/** The slice of /api/public/programs/availability the line consumes. */
export interface NextSessionAvailabilityData {
  closedDatesByLocation?: Record<string, string[]> | null;
  memberCounts?: Record<string, number> | null;
  dropInsByDateAndSection?: Record<string, Record<string, number>> | null;
}

/** The slice of the program payload's sections the line consumes. */
export interface NextSessionSectionInput {
  id: string;
  capacity?: number | string | null;
  /** Section location — scopes gym_closings to this section's slots. */
  location_id?: string | null;
  schedule?: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    /** Row-window bounds ('YYYY-MM-DD'); occurrences outside are skipped. */
    effective_date?: string | null;
    end_date?: string | null;
  }> | null;
}

export interface BuildNextSessionLineArgs {
  sections: NextSessionSectionInput[] | null | undefined;
  /** classes.unavailable_dates from the program payload (always known). */
  unavailableDates?: string[] | null;
  /**
   * Availability response, or null while the fetch is pending or after it
   * failed / was members-only-rejected — null renders time-only.
   */
  availability: NextSessionAvailabilityData | null;
  /** Program-level rule + extras (PostgREST may deliver numerics as strings). */
  availabilityRule?: string | null;
  extraDropInsAllowed?: number | string | null;
  gymNow: GymNowParts;
  /**
   * Session/season bounds from the program payload (session_start_date /
   * session_end_date), when the offering is session-based. Pre-season, the
   * line shows the season's FIRST session (more honest than tonight's date or
   * nothing); past the end date it returns null. Null/absent = ongoing.
   */
  sessionStartDate?: string | null;
  sessionEndDate?: string | null;
  /**
   * End of the window the availability response actually covers ('YYYY-MM-DD',
   * = today + lookahead). When the chosen occurrence falls AFTER this date — a
   * pre-season session starting beyond the lookahead — the spots fragment is
   * omitted rather than computed from availability data that never covered the
   * date. Absent = no restriction (back-compat).
   */
  availabilityWindowEndDate?: string | null;
}

export interface NextSessionLine {
  date: string; // 'YYYY-MM-DD'
  dayLabel: string; // 'Tonight' | 'Today' | 'Tomorrow' | 'Sun, Jul 19'
  startTime: string; // raw 'HH:mm[:ss]' — caller formats for display
  endTime: string;
  sectionId: string;
  /** '12 spots left' | '1 spot left' | 'Full' — or null for no fragment. */
  spotsText: string | null;
}

const VALID_RULES: DropInAvailabilityRule[] = ['always', 'only_if_not_full', 'extra_slots'];

/**
 * Normalize a payload date to 'YYYY-MM-DD' (tolerates ISO timestamps), or null
 * when absent/malformed. String-only — date-only values must never round-trip
 * through `new Date()` (UTC-midnight footgun).
 */
function toDateOnly(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const head = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : null;
}

/**
 * Compute the line's content, or null when no occurrence exists within the
 * lookahead (fully closed / no schedules) — the caller renders nothing then.
 */
export function buildNextSessionLine(args: BuildNextSessionLineArgs): NextSessionLine | null {
  const sections = args.sections || [];
  const sessionStart = toDateOnly(args.sessionStartDate);
  const sessionEnd = toDateOnly(args.sessionEndDate);

  // Flatten section schedule rows into slots that carry their section id (the
  // detail payload already drops rows inactive / out-of-window as of TODAY
  // for open gyms — the forwarded bounds cover rows expiring inside the
  // lookahead).
  const slots: Array<Partial<OpenGymSessionSlot>> = [];
  for (const section of sections) {
    if (!section?.id) continue;
    for (const row of section.schedule || []) {
      slots.push({
        day_of_week: row?.day_of_week,
        start_time: row?.start_time,
        end_time: row?.end_time,
        effective_date: toDateOnly(row?.effective_date),
        end_date: toDateOnly(row?.end_date),
        locationId: section.location_id ?? null,
        sectionId: section.id,
      });
    }
  }

  // Global closed set: the payload's own blackouts apply to every slot.
  const closed = new Set<string>(args.unavailableDates || []);

  // Location-scoped closures: once the availability response lands, its
  // closedDatesByLocation map (unavailable_dates + gym_closings per section
  // location, 'null' for no-location sections — same keying DropInCalendar
  // reads) is matched per slot, so a closure at location B never hides a
  // same-day session at location A.
  let closedByLocation: Map<string, Set<string>> | null = null;
  const byLocation = args.availability?.closedDatesByLocation;
  if (byLocation) {
    closedByLocation = new Map();
    for (const [loc, dates] of Object.entries(byLocation)) {
      closedByLocation.set(loc, new Set(Array.isArray(dates) ? dates : []));
    }
  }

  // Pre-season: scan from the LATER of gym-now and the session start, so the
  // page advertises the season's first session instead of a date that isn't
  // bookable yet. Lexicographic compare — both sides are 'YYYY-MM-DD'.
  const scanFrom: GymNowParts =
    sessionStart && sessionStart > args.gymNow.dateStr
      ? { dateStr: sessionStart, minutesOfDay: 0 }
      : args.gymNow;

  const occurrence = nextOpenGymSession(slots, closed, scanFrom, closedByLocation);
  if (!occurrence) return null;

  // Season over (or the soonest occurrence lands past the end date): no line.
  // The scan returns the soonest occurrence, so anything after it is later too.
  if (sessionEnd && occurrence.date > sessionEnd) return null;

  const dayLabel = formatSessionDayLabel(
    args.gymNow.dateStr,
    occurrence.date,
    occurrence.slot.start_time
  );

  // Spots fragment only when the availability response is present AND the chosen
  // date is inside the window that response covers (item 4b): a pre-season
  // session beyond the lookahead would otherwise read spots from data that never
  // included it. Absent window bound = no restriction (back-compat).
  const withinAvailabilityWindow =
    !args.availabilityWindowEndDate || occurrence.date <= args.availabilityWindowEndDate;
  let spotsText: string | null = null;
  if (args.availability && withinAvailabilityWindow) {
    const section = sections.find((s) => s?.id === occurrence.sectionId);
    const capacity = Number(section?.capacity);
    // No usable capacity (null/0/NaN) → no fragment rather than a bogus "Full".
    if (Number.isFinite(capacity) && capacity > 0) {
      const rule: DropInAvailabilityRule = VALID_RULES.includes(
        args.availabilityRule as DropInAvailabilityRule
      )
        ? (args.availabilityRule as DropInAvailabilityRule)
        : 'always';

      const spots = dropInSpotsForDate({
        dateStr: occurrence.date,
        sectionId: occurrence.sectionId,
        capacity,
        availabilityRule: rule,
        extraDropInsAllowed: Number(args.extraDropInsAllowed) || 0,
        memberCounts: args.availability.memberCounts || {},
        dropInsByDateAndSection: args.availability.dropInsByDateAndSection || {},
      });

      // Zero reads "Full" — never "0 spots left".
      spotsText = spots === 0 ? 'Full' : `${spots} ${spots === 1 ? 'spot' : 'spots'} left`;
    }
  }

  return {
    date: occurrence.date,
    dayLabel,
    startTime: occurrence.slot.start_time,
    endTime: occurrence.slot.end_time,
    sectionId: occurrence.sectionId,
    spotsText,
  };
}
